import React, { useState, useEffect, useRef } from 'react';
import { API_BASE } from '@/config/api';
import { motion } from 'framer-motion';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { Video, Calendar as CalendarIcon, Loader2, CheckCircle2, Clock, Shield, Sparkles, Stethoscope, User, XCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import CareFeatureNav from '@/components/CareFeatureNav';
import CarePageHeader from '@/components/CarePageHeader';
import MetaTags from '@/components/SEO/MetaTags';
import axios from 'axios';
import { format } from 'date-fns';
import { getAuthToken } from '@/utils/auth';
import { startTeleconsultationCheckout } from '@/utils/payments/subscribe';
import './teleconsultation.css';

const API = API_BASE;
const CONSULTATION_FEE_USD = 199;
const SELECT_CONTENT =
  'by-select-content bg-cream-paper border-hair text-forest-ink p-1.5 max-h-72 overflow-y-auto rounded-2xl shadow-lg';
const SELECT_ITEM =
  'text-forest-ink cursor-pointer px-3 py-2.5 my-0.5 rounded-xl focus:bg-mist focus:text-forest-ink focus:outline-none data-[highlighted]:bg-mist data-[highlighted]:text-forest-ink';
const FIELD_INPUT =
  'w-full h-12 bg-paper border-hair text-forest-ink rounded-2xl px-4 placeholder:text-[#3d4a42] focus-visible:ring-0 focus:border-forest-ink text-sm';

// Doctor types/specializations
const DOCTOR_TYPES = [
  'Surgical Oncologist',
  'Medical Oncologist',
  'Radiation Oncologist',
  'Pediatric Oncologist',
  'Gynecologic Oncologist',
  'Neuro-Oncologist',
  'Hematologic Oncologist',
  'Head & Neck Oncologist',
  'Thoracic Oncologist',
  'Urologic Oncologist',
  'Orthopedic Oncologist',
  'Dermatologic Oncologist'
];

export default function TeleconsultationPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);
  const [cancerTypes, setCancerTypes] = useState([]);
  const [loadingCancerTypes, setLoadingCancerTypes] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [errors, setErrors] = useState({});
  const [paymentFailed, setPaymentFailed] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const paymentSettledRef = useRef(false);
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const [formData, setFormData] = useState({
    patient_name: '',
    patient_email: '',
    country_code: '+1',
    patient_phone: '',
    doctor_type: '', // Changed from hospital_id
    appointment_type: 'teleconsultation',
    preferred_date: '',
    cancer_type: '',
    notes: ''
  });

  // Common country codes
  const countryCodes = [
    { code: '+1', country: 'USA', flag: '🇺🇸' },
    { code: '+91', country: 'India', flag: '🇮🇳' },
    { code: '+44', country: 'UK', flag: '🇬🇧' },
    { code: '+61', country: 'Australia', flag: '🇦🇺' },
    { code: '+971', country: 'UAE', flag: '🇦🇪' },
    { code: '+65', country: 'Singapore', flag: '🇸🇬' },
    { code: '+60', country: 'Malaysia', flag: '🇲🇾' },
    { code: '+66', country: 'Thailand', flag: '🇹🇭' },
    { code: '+86', country: 'China', flag: '🇨🇳' },
    { code: '+81', country: 'Japan', flag: '🇯🇵' },
    { code: '+82', country: 'South Korea', flag: '🇰🇷' },
    { code: '+49', country: 'Germany', flag: '🇩🇪' },
    { code: '+33', country: 'France', flag: '🇫🇷' },
    { code: '+39', country: 'Italy', flag: '🇮🇹' },
    { code: '+34', country: 'Spain', flag: '🇪🇸' },
    { code: '+31', country: 'Netherlands', flag: '🇳🇱' },
    { code: '+46', country: 'Sweden', flag: '🇸🇪' },
    { code: '+47', country: 'Norway', flag: '🇳🇴' },
    { code: '+41', country: 'Switzerland', flag: '🇨🇭' },
    { code: '+27', country: 'South Africa', flag: '🇿🇦' },
  ];
  const selectedCountry = countryCodes.find((country) => country.code === formData.country_code) || countryCodes[0];

  // Fetch cancer types from backend - same approach as RareCancersPage
  useEffect(() => {
    const fetchCancerTypes = async () => {
      setLoadingCancerTypes(true);
      try {
        const response = await axios.get(`${API}/cancer-types`, {
          timeout: 10000,
          headers: {
            'Content-Type': 'application/json',
          }
        });
        
        
        const data = response.data;
        let allCancers = [];
        
        // Handle structured format: { rare_cancers, common_cancers, all_cancers }
        if (data && (data.rare_cancers || data.common_cancers || data.all_cancers)) {
          
          // Combine all sources - prioritize all_cancers if available
          if (data.all_cancers && Array.isArray(data.all_cancers) && data.all_cancers.length > 0) {
            allCancers = [...data.all_cancers];
        } else {
            // Combine rare and common cancers
            const rare = Array.isArray(data.rare_cancers) ? data.rare_cancers : [];
            const common = Array.isArray(data.common_cancers) ? data.common_cancers : [];
            allCancers = [...rare, ...common];
          }
        }
        // Handle flat array format
        else if (Array.isArray(data) && data.length > 0) {
          allCancers = data;
        }
        
        // Remove duplicates based on name
        const uniqueCancers = allCancers.reduce((acc, current) => {
          if (current && current.name) {
            const existing = acc.find(item => item.name === current.name);
            if (!existing) {
              acc.push(current);
            }
          }
          return acc;
        }, []);
        
        // Sort alphabetically by name for better UX
        uniqueCancers.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setCancerTypes(uniqueCancers);
      } catch (error) {
        console.error('Error fetching cancer types:', error);
        setCancerTypes([]);
      } finally {
        setLoadingCancerTypes(false);
      }
    };
    fetchCancerTypes();
  }, []);

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    if (!formData.patient_name.trim()) {
      newErrors.patient_name = 'Full name is required';
      isValid = false;
    }

    if (!formData.patient_email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.patient_email)) {
      newErrors.patient_email = 'Valid email is required';
      isValid = false;
    }

    const fullPhone = `${formData.country_code || '+1'}${formData.patient_phone}`.replace(/\s+/g, '');
    if (!formData.patient_phone.trim() || fullPhone.length < 12) {
      newErrors.patient_phone = 'Valid phone number is required';
      isValid = false;
    }

    if (!formData.cancer_type) {
      newErrors.cancer_type = 'Cancer type is required';
      isValid = false;
    }

    if (!formData.doctor_type) {
      newErrors.doctor_type = 'Doctor type is required';
      isValid = false;
    }

    if (!selectedDate) {
      newErrors.preferred_date = 'Preferred date is required';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setPaymentFailed(false);
    setPaymentError('');

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    paymentSettledRef.current = false;
    const phoneDigits = String(formData.patient_phone || '').replace(/\D/g, '');
    try {
      await startTeleconsultationCheckout({
        prefill: {
          name: formData.patient_name,
          email: formData.patient_email,
          contact: phoneDigits,
        },
        onPaid: async (payment) => {
          paymentSettledRef.current = true;
          try {
            const submitData = {
              ...formData,
              patient_phone: `${formData.country_code} ${formData.patient_phone}`.trim(),
              preferred_date: selectedDate ? format(selectedDate, 'yyyy-MM-dd') : '',
              razorpay_order_id: payment.razorpay_order_id,
              razorpay_payment_id: payment.razorpay_payment_id,
              razorpay_signature: payment.razorpay_signature,
            };
            const token = getAuthToken();
            const response = await axios.post(`${API}/appointments`, submitData, {
              withCredentials: true,
              headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            setResult(response.data);
            setSubmitted(true);
          } catch (error) {
            setPaymentFailed(true);
            setPaymentError(
              error.response?.data?.detail
              || 'Payment went through, but the appointment could not be saved. Write to care@getbyonco.com with your payment ID.'
            );
          } finally {
            setLoading(false);
          }
        },
        onDismiss: () => {
          if (paymentSettledRef.current) return;
          setLoading(false);
          setPaymentFailed(true);
          setPaymentError('Payment was cancelled. No appointment was booked.');
        },
        onFail: (error) => {
          if (paymentSettledRef.current) return;
          setLoading(false);
          setPaymentFailed(true);
          setPaymentError(error?.message || 'Payment failed. No appointment was booked.');
        },
      });
    } catch (error) {
      setLoading(false);
      setPaymentFailed(true);
      setPaymentError(error?.message || 'Could not start payment. No appointment was booked.');
    }
  };

  if (paymentFailed && !submitted) {
    return (
      <div className="care-page min-h-screen bg-cream-paper py-8 sm:py-12 px-4 sm:px-6">
        <div className="max-w-xl mx-auto">
          <CareFeatureNav current="/teleconsultation" />
          <Card className="bg-paper border-hair rounded-3xl overflow-visible">
            <CardHeader className="text-center px-6 pt-8">
              <div className="mx-auto mb-4 bg-mist border border-hair rounded-full h-20 w-20 flex items-center justify-center">
                <XCircle className="h-10 w-10 text-forest-ink" />
              </div>
              <CardTitle className="text-forest-ink mb-2">
                Payment unsuccessful
              </CardTitle>
              <CardDescription className="text-[#3d4a42]">
                No appointment was booked.
              </CardDescription>
            </CardHeader>
            <CardContent className="px-6 pb-8 space-y-4">
              <div className="bg-cream-paper border border-hair rounded-2xl p-4 text-sm text-forest-ink leading-relaxed">
                {paymentError || 'The payment did not complete. Your details were not saved as a booking.'}
              </div>
              <Button
                className="w-full bg-forest-ink hover:bg-forest-shadow text-cream-paper min-h-[44px]"
                onClick={() => {
                  setPaymentFailed(false);
                  setPaymentError('');
                }}
              >
                Try payment again
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (submitted && result) {
    return (
      <div className="care-page min-h-screen bg-cream-paper py-8 sm:py-12 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <CareFeatureNav current="/teleconsultation" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            <Card className="bg-paper border-hair rounded-3xl overflow-visible">
              <CardHeader className="text-center px-6 pt-8">
                <motion.div
                  className="flex justify-center mb-4"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
                >
                  <div className="bg-keylime-wash border-2 border-border-mist rounded-full h-24 w-24 flex items-center justify-center ">
                    <CheckCircle2 className="h-12 w-12 text-forest-ink" />
                  </div>
                </motion.div>
                <CardTitle className="text-forest-ink mb-2 leading-tight">
                  Teleconsultation booked
                </CardTitle>
                <CardDescription className="text-[#3d4a42]">
                  Payment received. Your appointment request is with our team.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6 px-6 pb-8">
                <div className="bg-cream-paper rounded-2xl p-6 space-y-4 border border-hair overflow-visible">
                  <div>
                    <div className="text-sm text-[#3d4a42] mb-1 font-medium">Booking ID</div>
                    <div className="text-forest-ink font-mono text-sm sm:text-base font-semibold break-all leading-relaxed pt-0.5">{result.id}</div>
                  </div>
                  <div>
                    <div className="text-sm text-[#3d4a42] mb-1 font-medium">Patient Name</div>
                    <div className="text-forest-ink text-lg font-semibold">{result.patient_name}</div>
                  </div>
                  {result.hospital_name && (
                    <div>
                      <div className="text-sm text-[#3d4a42] mb-1 font-medium">Hospital</div>
                      <div className="text-forest-ink text-lg font-semibold">{result.hospital_name}</div>
                    </div>
                  )}
                  {result.razorpay_payment_id && (
                    <div>
                      <div className="text-sm text-[#3d4a42] mb-1 font-medium">Payment ID</div>
                      <div className="text-forest-ink font-mono text-sm break-all">{result.razorpay_payment_id}</div>
                    </div>
                  )}
                  <div>
                    <div className="text-sm text-[#3d4a42] mb-1 font-medium">Status</div>
                    <Badge className="bg-mist text-forest-ink border-hair px-3 py-1 font-medium">
                      Paid · awaiting confirmation
                    </Badge>
                  </div>
                </div>

                <div className="bg-sage-mist border border-border-mist rounded-xl p-6">
                  <h4 className="text-forest-ink font-semibold mb-3 flex items-center gap-2 text-lg">
                    <Clock className="h-5 w-5 text-forest-ink" />
                    What happens next?
                  </h4>
                  <ul className="space-y-2 text-forest-ink text-sm font-medium">
                    <li className="flex items-start gap-2">
                      <span className="text-forest-ink mt-1 font-bold">•</span>
                      Our team will contact you within 2 hours to confirm your appointment
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-forest-ink mt-1 font-bold">•</span>
                      You'll receive a video call link via email/SMS
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-forest-ink mt-1 font-bold">•</span>
                      Please keep your medical reports ready for the consultation
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-forest-ink mt-1 font-bold">•</span>
                      The consultation will be conducted by a certified oncologist
                    </li>
                  </ul>
                </div>

                <div className="text-center text-forest-ink text-sm bg-mist rounded-2xl p-4 font-medium">
                  <p>{result.message}</p>
                </div>

                <Button 
                  className="w-full bg-forest-ink hover:bg-forest-shadow text-cream-paper py-4 sm:py-6 text-base sm:text-lg min-h-[44px]"
                  onClick={() => navigate('/')}
                >
                  Return to Home
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="care-page min-h-screen bg-cream-paper pt-8 sm:pt-10 pb-8 sm:pb-12 px-4 sm:px-6">
      <MetaTags
        title="Online Oncologist Video Consultation | Cancer Telehealth | ByOnco"
        description="Book a secure online video consultation with a board-certified oncologist. Review diagnosis, treatment plans, and second opinions from home."
        path="/teleconsultation"
      />
      <div className="max-w-4xl mx-auto">
        <CarePageHeader
          current="/teleconsultation"
          kicker="Video consult"
          title="Book a video consult"
          lede="Talk with an oncologist from anywhere on a secure video call."
        />

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
        >
          <Card className="bg-paper border-hair rounded-3xl p-0">
            <CardHeader className="px-4 sm:px-6 pt-4 sm:pt-6">
              <CardTitle className="text-forest-ink break-words">Schedule your video consultation</CardTitle>
              <CardDescription className="text-[#3d4a42] mt-1">
                Fill in your details and we will connect you with a specialist.
              </CardDescription>
            </CardHeader>
            <CardContent className="px-4 sm:px-6 pb-4 sm:pb-6">
              <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                {errors.submit && (
                  <div className="bg-mist border border-hair text-forest-ink rounded-2xl p-4 text-sm">
                    {errors.submit}
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 items-start">
                  <div>
                    <Label className="text-forest-ink mb-1.5 block font-medium">
                      Full name <span className="text-red-400">*</span>
                    </Label>
                    <div className="relative">
                      <User className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-forest-ink z-10 pointer-events-none" />
                      <Input
                        required
                        placeholder="Enter your full name"
                        className={`${FIELD_INPUT} pr-3`}
                        style={{ paddingLeft: '2.75rem' }}
                        value={formData.patient_name}
                        onChange={(e) => {
                          setFormData({...formData, patient_name: e.target.value});
                          if (errors.patient_name) setErrors({...errors, patient_name: ''});
                        }}
                      />
                    </div>
                    {errors.patient_name && (
                      <p className="text-xs sm:text-sm text-red-400 mt-1">{errors.patient_name}</p>
                    )}
                  </div>

                  <div>
                    <Label className="text-forest-ink mb-1.5 block font-medium">
                      Email <span className="text-red-400">*</span>
                    </Label>
                    <Input
                      required
                      type="email"
                      placeholder="your.email@example.com"
                      className={FIELD_INPUT}
                      value={formData.patient_email}
                      onChange={(e) => {
                        setFormData({...formData, patient_email: e.target.value});
                        if (errors.patient_email) setErrors({...errors, patient_email: ''});
                      }}
                    />
                    {errors.patient_email && (
                      <p className="text-xs sm:text-sm text-red-400 mt-1">{errors.patient_email}</p>
                    )}
                  </div>

                  <div className="md:col-span-2">
                    <Label className="text-forest-ink mb-1.5 block font-medium">
                      Phone number <span className="text-red-400">*</span>
                    </Label>
                    <div className={`tc-phone ${errors.patient_phone ? 'is-error' : ''}`}>
                      <Select
                        value={formData.country_code}
                        onValueChange={(value) => {
                          setFormData({...formData, country_code: value});
                        }}
                      >
                        <SelectTrigger className="tc-phone-code" aria-label="Country code">
                          <span className="tc-phone-flag" aria-hidden="true">{selectedCountry.flag}</span>
                          <span className="tc-phone-dial">{selectedCountry.code}</span>
                        </SelectTrigger>
                        <SelectContent className={`${SELECT_CONTENT} tc-phone-menu`} position="popper" sideOffset={8} style={{ minWidth: 260 }}>
                          {countryCodes.map((country) => (
                            <SelectItem 
                              key={country.code} 
                              value={country.code} 
                              className={SELECT_ITEM}
                            >
                              <span className="tc-phone-option">
                                <span className="tc-phone-flag">{country.flag}</span>
                                <span className="tc-phone-dial">{country.code}</span>
                                <span className="tc-phone-name">{country.country}</span>
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <span className="tc-phone-divider" aria-hidden="true" />
                      <Input
                        required
                        type="tel"
                        inputMode="tel"
                        placeholder="(555) 123-4567"
                        className="tc-phone-input"
                        value={formData.patient_phone}
                        onChange={(e) => {
                          setFormData({...formData, patient_phone: e.target.value});
                          if (errors.patient_phone) setErrors({...errors, patient_phone: ''});
                        }}
                      />
                    </div>
                    {errors.patient_phone && (
                      <p className="text-xs sm:text-sm text-red-400 mt-1">{errors.patient_phone}</p>
                    )}
                  </div>

                  <div className="md:col-span-2">
                    <Label className="text-forest-ink mb-1.5 block font-medium">
                      Cancer type <span className="text-red-400">*</span>
                    </Label>
                    <Select 
                      required 
                      value={formData.cancer_type}
                      onValueChange={(value) => {
                        setFormData({...formData, cancer_type: value});
                        if (errors.cancer_type) setErrors({...errors, cancer_type: ''});
                      }}
                    >
                      <SelectTrigger className={`${FIELD_INPUT} justify-between`}>
                        <SelectValue placeholder="Select cancer type" className="text-forest-ink" />
                      </SelectTrigger>
                      <SelectContent className={SELECT_CONTENT} position="popper" sideOffset={6}>
                        {loadingCancerTypes ? (
                          <SelectItem value="loading" disabled className={SELECT_ITEM}>
                            Loading cancer types...
                          </SelectItem>
                        ) : cancerTypes.length > 0 ? (
                          cancerTypes.map((cancer, index) => (
                            <SelectItem 
                              key={cancer.id || cancer.name || `cancer-${index}`} 
                              value={cancer.name} 
                              className={SELECT_ITEM}
                            >
                              {cancer.name}
                            </SelectItem>
                          ))
                        ) : (
                          <SelectItem value="no-data" disabled className={SELECT_ITEM}>
                            No cancer types available. Please check backend connection.
                          </SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                    {errors.cancer_type && (
                      <p className="text-sm text-red-400 mt-1">{errors.cancer_type}</p>
                    )}
                  </div>

                  <div>
                    <Label className="text-forest-ink mb-1.5 block font-medium">
                      Type of doctor <span className="text-red-400">*</span>
                    </Label>
                    <Select 
                      required 
                      value={formData.doctor_type}
                      onValueChange={(value) => {
                        setFormData({...formData, doctor_type: value});
                        if (errors.doctor_type) setErrors({...errors, doctor_type: ''});
                      }}
                    >
                      <SelectTrigger className={`${FIELD_INPUT} justify-between`}>
                        <SelectValue placeholder="Choose doctor type" className="text-forest-ink" />
                      </SelectTrigger>
                      <SelectContent className={SELECT_CONTENT} position="popper" sideOffset={6}>
                        {DOCTOR_TYPES.map((type) => (
                          <SelectItem 
                            key={type} 
                            value={type} 
                            className={SELECT_ITEM}
                          >
                            {type}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.doctor_type && (
                      <p className="text-sm text-red-400 mt-1">{errors.doctor_type}</p>
                    )}
                  </div>

                  <div>
                    <Label className="text-forest-ink mb-1.5 block font-medium">
                      Preferred date <span className="text-red-400">*</span>
                    </Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          className={`w-full justify-start text-left font-normal h-12 bg-paper border-hair text-forest-ink hover:bg-mist rounded-2xl px-4 ${
                            errors.preferred_date ? 'border-[#c45c4a]' : ''
                          }`}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {selectedDate ? format(selectedDate, 'PPP') : 'Pick a date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent
                        className="p-0 bg-paper border-hair rounded-2xl z-[400]"
                        align="start"
                        style={{ width: 320, minWidth: 320, maxWidth: 320 }}
                      >
                        <Calendar
                          selected={selectedDate}
                          onSelect={(date) => {
                            setSelectedDate(date);
                            if (errors.preferred_date) setErrors({...errors, preferred_date: ''});
                          }}
                          disabled={(date) => {
                            const dateToCompare = new Date(date);
                            dateToCompare.setHours(0, 0, 0, 0);
                            return dateToCompare < today;
                          }}
                        />
                      </PopoverContent>
                    </Popover>
                    {errors.preferred_date && (
                      <p className="text-sm text-red-400 mt-1">{errors.preferred_date}</p>
                    )}
                  </div>
                </div>

                <div>
                  <Label className="text-forest-ink mb-2 block font-medium">Additional Notes (Optional)</Label>
                  <Textarea
                    placeholder="Any specific concerns or questions"
                    className="w-full min-h-[100px] bg-paper border-hair text-forest-ink rounded-2xl px-4 py-3 placeholder:text-[#3d4a42] focus-visible:ring-0 focus:border-forest-ink text-sm"
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    rows={4}
                  />
                </div>

                <div className="bg-mist border border-hair rounded-2xl p-4">
                  <p className="text-forest-ink">
                    Consultation fee: <strong>${CONSULTATION_FEE_USD}</strong> for a 30-minute video visit with a specialist.
                  </p>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-forest-ink hover:bg-forest-shadow text-cream-paper text-sm font-medium py-3 min-h-[48px]"
                  disabled={loading}
                >
                  {loading ? (
                    <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Booking...</>
                  ) : (
                    <>
                      <Video className="h-5 w-5 mr-2" />
                      Pay ${CONSULTATION_FEE_USD} & book
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </motion.div>

        {/* Benefits */}
        <motion.div
          className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 mt-8 sm:mt-12"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.6 }}
        >
          {[
            {
              icon: <Video className="h-6 w-6 text-forest-ink" />,
              title: "HD Video Call",
              description: "Secure, encrypted video consultations"
            },
            {
              icon: <Shield className="h-6 w-6 text-forest-ink" />,
              title: "Expert Oncologists",
              description: "Board-certified specialists with 15+ years experience"
            },
            {
              icon: <Clock className="h-6 w-6 text-forest-ink" />,
              title: "Flexible Timing",
              description: "Book at your convenience, available 7 days a week"
            }
          ].map((feature, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 + idx * 0.1, duration: 0.5 }}
              whileHover={{ scale: 1.05, y: -5 }}
            >
              <Card className="bg-paper border-hair rounded-3xl h-full">
                <CardHeader>
                  <CardTitle className="text-forest-ink flex items-center gap-2 font-semibold" style={{ fontFamily: "'Instrument Sans', system-ui, sans-serif" }}>
                    {feature.icon}
                    {feature.title}
                  </CardTitle>
                  <CardDescription className="text-[#3d4a42] font-medium">
                    {feature.description}
                  </CardDescription>
                </CardHeader>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </div>
  );
}
